import { Grid, SelectChangeEvent, Typography } from '@mui/material';
import { ProceedSaveLater } from './components';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import { MFTextField } from '../../lib/formik';
import { ApplicationProps, DistributorListType } from '../../redux-store/types/api-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { updateApplication } from '../../redux-store/actions/application';
import { useHistory } from 'react-router';
import { ClassPlanProps, FundProps } from '../../redux-store/types/funds';
import { investmentPaymentDetailsSchema } from '../../utils/schema';
import {
  AMC_APPROVER_CHECK_FOR_INDIVIDUAL,
  APPLICATION_TYPE,
  defaultCurrency,
  FLOW_TYPE_MASTER,
  investmentPlanTypeMaster,
  SAVE_LATER_VALIDATION_CHECK,
  USER_ROLES,
  yesNoMaster,
} from '../../utils/constant';
import {
  allowOnlyNumbers,
  applicationComparison,
  applyRoleBasedStatus,
  checkIfApplicationIsNonIndividual,
  currencyConversion,
  folioValidationCheck,
  saveForLater,
  getStep,
  PlacementFeeApplicable,
  IsInvestorWithPAN,
  isFieldApplicable,
  IsOutBoundFundFlow,
  isUSTechFund,
} from '../../utils/utilityFunctions';
import { useSnackbar } from 'notistack';
import { getDirectApplicableFunds } from '../../redux-store/actions/funds';
import { getDistributorById } from '../../redux-store/actions/userManagement';
import { SearchableSelect } from '../../lib/formik/searchSelectField';
import FieldValidationNote from './FieldValidationNote';
import { KeyValue } from '../ApplicationDetails/InvestorWithoutPan/contributor';

type Values = Partial<ApplicationProps> & {
  saveType: string;
};

const calculatePlacementFeeAmount = (commitmentAmount: number, placementFee: number): number =>
  Math.round(((commitmentAmount * placementFee) / 100 + Number.EPSILON) * 100) / 100;

const fundInitialValues = {
  schemeId: '',
  planId: '',
  commitmentAmount: 0,
  setupFeePercentage: 0,
  managementFeesRate: 0,
  taxRate: 0,
  drawDownNumber: 0,
  contributionChequeAmount: 0,
  hurdleRateOfReturns: '',
  performanceFee: '',
  commitmentAmountForMigration: '',
  contributionAmountForMigration: '',
  placementFee: 0,
  totalExpense: 0,
  totalAmountPayable: null,
  placementFeeAmount: null,
  operatingExpenseApplicable: '',
  operatingExpenseInWords: '',
  exitLoadApplicable: '',
  exitLoadInWords: '',
  placementFeeType: '',
  investmentPlanType: '',
  investmentPlanApplicable: '',
};

const initialValues: Values = {
  ...fundInitialValues,
  modeOfPayment: 'cheque',
  paymentRefNo: '',
  chequeDate: '',
  paymentBankAccountNumber: '',
  paymentBankame: '',
  paymentBankAccontType: '',
  saveType: 'save and proceed',
  applicationSource: 'digitalonboarding',
};

export default function InvestmentPaymentDetails(): JSX.Element {
  // const [open, setOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [fundsList, setFundsList] = useState<FundProps[]>([]);
  const [selectedFund, setSelectedFund] = useState<FundProps | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Partial<ClassPlanProps> | null>(null);
  const { application } = useSelector((store: RootStateType) => store.application);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [paymentDetails, setPaymentDetails] = useState(initialValues);
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { flowType = '', fundFlowType = '' } = application || {};
  const disableSchemeId = FLOW_TYPE_MASTER[flowType || ''] === FLOW_TYPE_MASTER.investor_with_pan;

  const {
    minCommitmentAmount = 10000000,
    maxCommitmentAmount = 20000000,
    minContributionPercentage = 10,
    managementFee = 0,
    currency = defaultCurrency,
    isPlacementFeeApplicable,
    minPlacementFee = 0,
    maxPlacementFee = 0,
  } = selectedPlan || {};

  useEffect(() => {
    const { plan } = application || ({} as ApplicationProps);
    const {
      minCommitmentAmount = 1,
      maxCommitmentAmount = 0,
      minContributionPercentage = 1,
      managementFee = 0,
      currency = defaultCurrency,
      transactionCurrency = defaultCurrency,
      isPlacementFeeApplicable,
      placementFee = 0,
      totalExpense,
      operatingExpenseApplicable = '',
      operatingExpenseInWords = '',
      exitLoadApplicable = '',
      exitLoadInWords = '',
      placementFeeType = '',
      minPlacementFee = 0,
      maxPlacementFee = 0,
      investmentPlanType = '',
      investmentPlanApplicable = '',
    } = plan || {};
    setSelectedPlan(
      plan
        ? {
            ...selectedPlan,
            minCommitmentAmount: Number(minCommitmentAmount),
            maxCommitmentAmount: Number(maxCommitmentAmount),
            minContributionPercentage: Number(minContributionPercentage),
            managementFee: Number(managementFee),
            currency,
            transactionCurrency: transactionCurrency || defaultCurrency,
            isPlacementFeeApplicable,
            placementFee: Number(placementFee),
            totalExpense,
            operatingExpenseApplicable,
            operatingExpenseInWords,
            exitLoadApplicable,
            exitLoadInWords,
            placementFeeType,
            minPlacementFee: Number(minPlacementFee),
            maxPlacementFee: Number(maxPlacementFee),
            investmentPlanApplicable,
            investmentPlanType,
          }
        : null
    );

    setPaymentDetails({
      ...paymentDetails,
      schemeId: application?.schemeId || '',
      planId: application?.planId || '',
      commitmentAmount:
        Number(application?.commitmentAmount) === 0
          ? application?.commitmentAmount
          : folioValidationCheck(application?.applicationSource || 'digitalonboarding')
          ? 0
          : application?.commitmentAmount || null,
      setupFeePercentage: application?.setupFeePercentage || 0,
      managementFeesRate: application?.managementFeesRate || 0,
      placementFee: application?.placementFee || 0,
      totalExpense: application?.totalExpense || 0,
      // taxRate: application?.taxRate || null,
      // drawDownNumber: application?.drawDownNumber || null,
      contributionChequeAmount: folioValidationCheck(
        application?.applicationSource || 'digitalonboarding'
      )
        ? 0
        : Number(application?.contributionChequeAmount) || 0,
      modeOfPayment: application?.modeOfPayment || '',
      paymentRefNo: application?.paymentRefNo || '',
      chequeDate: application?.chequeDate || null,
      paymentBankAccountNumber: application?.paymentBankAccountNumber || '',
      paymentBankame: application?.paymentBankame || '',
      paymentBankAccontType: application?.paymentBankAccontType || '',
      hurdleRateOfReturns: application?.hurdleRateOfReturns || '',
      // performanceFee: application?.performanceFee || '',
      commitmentAmountForMigration: folioValidationCheck(
        application?.applicationSource || 'digitalonboarding'
      )
        ? application?.commitmentAmountForMigration || ''
        : '',
      contributionAmountForMigration: folioValidationCheck(
        application?.applicationSource || 'digitalonboarding'
      )
        ? application?.contributionAmountForMigration || ''
        : '',
      applicationSource: application?.applicationSource || 'digitalonboarding',
      totalAmountPayable: application?.totalAmountPayable || 0,
      placementFeeAmount: application?.placementFeeAmount || 0,
      operatingExpenseApplicable: application?.operatingExpenseApplicable || '',
      operatingExpenseInWords: application?.operatingExpenseInWords || '',
      exitLoadApplicable: application?.exitLoadApplicable || '',
      exitLoadInWords: application?.exitLoadInWords || '',
      placementFeeType: application?.placementFeeType || '',
      isPlacementFeeApplicable: application?.isPlacementFeeApplicable || '',
      investmentPlanType: application?.investmentPlanType || '',
      investmentPlanApplicable: application?.investmentPlanApplicable || '',
      transactionCurrency: application?.transactionCurrency || '',
    });
  }, [application]);

  const handleSubmit = async (values: Values) => {
    try {
      const {
        id,
        applicant1ReferenceId = '',
        currentStep,
        applicationType,
        status,
        hasPOA,
        applicationNumber,
      } = application || {};
      const { saveType, ...rest } = values;
      const checkApplication = applicationComparison(
        { ...application, setupFeePercentage: Number(application?.setupFeePercentage) },
        {
          ...application,
          ...rest,
          contributionChequeAmount: Number(rest.contributionChequeAmount) || 0,
          setupFeePercentage: Number(rest.setupFeePercentage),
          placementFee: isFieldApplicable(rest?.isPlacementFeeApplicable || '')
            ? Number(rest.placementFee)
            : null,
          operatingExpenseInWords: isFieldApplicable(rest?.operatingExpenseApplicable || '')
            ? rest.operatingExpenseInWords
            : null,
          exitLoadInWords: isFieldApplicable(rest?.exitLoadApplicable || '')
            ? rest.exitLoadInWords
            : null,
          currentStep:
            applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
              ? !!currentStep && currentStep > 3
                ? currentStep
                : Number(currentStep) + 1
              : !!currentStep && currentStep > 7
              ? currentStep
              : Number(currentStep) + 1,

          investmentPlanType: isFieldApplicable(rest?.investmentPlanApplicable || '')
            ? rest.investmentPlanType
            : '',
        }
      );
      const isSaveLater = saveType !== 'save and proceed';
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              ...rest,
              schemeId: rest.schemeId || null,
              planId: rest.planId || null,
              isOnlyXalt:
                selectedFund?.isOnlyXalt ??
                fundsList.find((f) => Number(f.id) === Number(rest.schemeId))?.isOnlyXalt ??
                false,
              contributionChequeAmount: Number(rest.contributionChequeAmount) || 0,
              setupFeePercentage: Number(rest.setupFeePercentage),
              placementFee: isFieldApplicable(rest?.isPlacementFeeApplicable || '')
                ? Number(rest.placementFee)
                : null,
              operatingExpenseInWords: isFieldApplicable(rest?.operatingExpenseApplicable || '')
                ? rest.operatingExpenseInWords
                : null,
              exitLoadInWords: isFieldApplicable(rest?.exitLoadApplicable || '')
                ? rest.exitLoadInWords
                : null,
              investmentPlanType: isFieldApplicable(rest?.investmentPlanApplicable || '')
                ? rest.investmentPlanType
                : '',
              status:
                !hasPOA &&
                AMC_APPROVER_CHECK_FOR_INDIVIDUAL &&
                status !== 'draft' &&
                applyRoleBasedStatus(role) &&
                applicationType !== APPLICATION_TYPE.NON_INDIVIDUAL
                  ? 'sent_to_amc_approver'
                  : status,
              currentStep:
                applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                  ? getStep(4, isSaveLater)
                  : getStep(8, isSaveLater),
              ...((!SAVE_LATER_VALIDATION_CHECK as boolean) && { saveForLater: isSaveLater }),
              // applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
              //   ? !!currentStep && currentStep > 3
              //     ? currentStep
              //     : Number(currentStep) + 1
              //   : !!currentStep && currentStep > 7
              //   ? currentStep
              //   : Number(currentStep) + 1,
            },
            applicationId: id,
            ...(isSaveLater && { toastMessage: '' }),
            flowType,
          })
        );
        !isSaveLater
          ? applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
            ? history.push('bank-details', { id, applicant1ReferenceId })
            : history.push('document-details', { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
        !isSaveLater
          ? applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
            ? history.push('bank-details', { id, applicant1ReferenceId })
            : history.push('document-details', { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      }
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    }
  };

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        const response = application?.distributor_id
          ? ((
              (await dispatch(
                getDistributorById(application?.distributor_id, {
                  ...(IsOutBoundFundFlow(fundFlowType)
                    ? { isInvestorWithPanApplicable: true }
                    : { isInvestorWithPanApplicable: false }),
                })
              )) as unknown as DistributorListType
            ).planDetails.schemes as FundProps[])
          : ((await dispatch(
              getDirectApplicableFunds({
                isActive: true,
                ...(IsOutBoundFundFlow(fundFlowType)
                  ? { isInvestorWithPanApplicable: true }
                  : { isInvestorWithPanApplicable: false }),
              })
            )) as unknown as FundProps[]);
        if (!isComponentAlive) {
          return;
        }
        const { scheme } = application || {};
        const updatedFundPlan =
          application?.schemeId &&
          (!application.scheme.isActive ||
            !(response.map((res) => res.id?.toString()) as unknown as string[]).includes(
              application?.schemeId?.toString()
            ))
            ? [
                ...response,
                {
                  ...application.scheme,
                  plans: [application.plan],
                } as unknown as FundProps,
              ]
            : response;
        setFundsList(updatedFundPlan);
        const updatePlans =
          application?.schemeId &&
          application?.scheme.isActive &&
          application?.planId &&
          !application.plan?.isActive
            ? ({
                ...updatedFundPlan.find((fund) => Number(fund.id) === Number(scheme?.id)),
                plans: [
                  ...(
                    updatedFundPlan.find(
                      (fund) => Number(fund.id) === Number(scheme?.id)
                    ) as unknown as FundProps
                  ).plans,
                  application.plan,
                ],
              } as unknown as FundProps)
            : updatedFundPlan.find((fund) => Number(fund.id) === Number(scheme?.id)) || null;
        setSelectedFund(updatePlans);
        if (updatePlans?.plans?.length === 1) {
          const onlyPlan = updatePlans?.plans[0];
          setSelectedPlan(onlyPlan);

          const commitmentAmount = onlyPlan?.minCommitmentAmount || 0;
          const placementFeeAmount = calculatePlacementFeeAmount(
            commitmentAmount,
            Number(onlyPlan?.placementFee) || 0
          );
          // const totalAmountPayable = IsOutBoundFundFlow(application?.fundFlowType || '')
          //   ? commitmentAmount + placementFeeAmount
          //   : commitmentAmount;
          const totalAmountPayable = commitmentAmount;

          setPaymentDetails((prev) => ({
            ...prev,
            planId: Number(onlyPlan?.id) as unknown as string,
            commitmentAmount: onlyPlan?.minCommitmentAmount,
            placementFeeAmount,
            totalAmountPayable,
            setupFeePercentage: onlyPlan?.setupFee || 0,
            placementFee: onlyPlan?.placementFee || 0,
            totalExpense: onlyPlan?.totalExpense || 0,
            managementFeesRate: onlyPlan?.managementFee || 0,
            operatingExpenseApplicable: onlyPlan?.operatingExpenseApplicable || '',
            operatingExpenseInWords: onlyPlan?.operatingExpenseInWords || '',
            exitLoadApplicable: onlyPlan?.exitLoadApplicable || '',
            exitLoadInWords: onlyPlan?.exitLoadInWords || '',
            placementFeeType: onlyPlan?.placementFeeType || '',
            isPlacementFeeApplicable: onlyPlan?.isPlacementFeeApplicable || '',
            investmentPlanApplicable: onlyPlan?.investmentPlanApplicable || '',
            investmentPlanType: onlyPlan?.investmentPlanType || '',
            transactionCurrency: onlyPlan?.transactionCurrency || '',
          }));
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      isComponentAlive = false;
    };
  }, [application]);

  const getClassPlanOptionsOrDetails = (detail?: string) => {
    if (detail) {
      return selectedFund?.[detail] || '';
    }
    return (
      selectedFund?.plans?.map((plan) => ({
        key: plan?.planDescription,
        value: Number(plan?.id),
      })) || []
    );
  };
  const hasSingleClassPlan =
    (getClassPlanOptionsOrDetails() as { key: string; value: string | number }[])?.length === 1;
  const isFieldDisabled = [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role);
  return (
    <Formik
      initialValues={paymentDetails}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validate={(values: Values) => {
        try {
          validateYupSchema(
            values,
            investmentPaymentDetailsSchema(
              minCommitmentAmount,
              maxCommitmentAmount,
              minContributionPercentage,
              managementFee,
              selectedPlan?.currency || '',
              values.saveType || '',
              isPlacementFeeApplicable || '',
              selectedPlan?.placementFee || 0,
              Number(minPlacementFee),
              Number(maxPlacementFee)
            ),
            true,
            values
          );
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}>
      {({ handleSubmit, values, setValues }) => (
        <Grid
          container
          rowSpacing={1}
          columnSpacing={5}
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}>
          <Grid item xs={12}>
            <SearchableSelect
              name={'schemeId'}
              label="Fund *"
              items={fundsList.map((fund) => ({ value: Number(fund.id), key: fund.schemeName }))}
              onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
                setValues({
                  ...values,
                  ...fundInitialValues,
                  schemeId: value as string,
                });
                setSelectedFund(fundsList.find((f) => Number(f.id) === value) || null);
                setSelectedPlan(null);
              }}
              disabled={isFieldDisabled}
              searchFieldPlaceholder={'Search Fund'}
            />
          </Grid>
          {/* {!!values.schemeId && (
            <Grid item xs={12}>
              <Box
                sx={{
                  boxShadow: '0px 0px 12px 2px rgba(0, 0, 0, 0.1)',
                  borderRadius: '10px',
                  p: { xs: '20px', sm: '30px 40px' },
                  my: 2,
                }}>
                <Box
                  onClick={() => setOpen(!open)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 600,
                      fontSize: 16,
                      color: 'primary.main',
                      letterSpacing: '0.05em',
                    }}>
                    Bank Details related to this fund (Scheme)
                  </Typography>
                  <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : '' }} />
                </Box>
                {open && (
                  <Grid
                    container
                    spacing={3}
                    sx={{
                      mt: 3,
                      '.MuiTypography-root': {
                        fontSize: 16,
                        letterSpacing: '0.05em',
                        '&.MuiTypography-body2 ': { fontWeight: 600 },
                      },
                    }}>
                    <Grid item xs={12} sm={6}>
                      <Typography gutterBottom>Bank Name</Typography>
                      <Typography variant="body2">
                        {getClassPlanOptionsOrDetails('bankName')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography gutterBottom>Bank Account Number</Typography>
                      <Typography variant="body2">
                        {getClassPlanOptionsOrDetails('bankAccountNumber')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography gutterBottom>IFSC Code</Typography>
                      <Typography variant="body2">
                        {getClassPlanOptionsOrDetails('ifscCode')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography gutterBottom>Bank Branch</Typography>
                      <Typography variant="body2">
                        {getClassPlanOptionsOrDetails('bankBranch')}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Grid>
          )} */}
          <Grid item xs={12} sm={6}>
            <SearchableSelect
              name={'planId'}
              label="Plan Type *"
              items={getClassPlanOptionsOrDetails() as [{ key: string; value: string | number }]}
              disabled={!values.schemeId || isFieldDisabled}
              onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
                const selectedPlan = selectedFund?.plans
                  ?.filter((plan) => plan?.isActive)
                  ?.find((p) => Number(p.id) === value);
                setSelectedPlan(selectedPlan || null);

                const commitmentAmount = selectedPlan?.minCommitmentAmount || 0;
                const placementFeeAmount =
                  (commitmentAmount * (selectedPlan?.placementFee || 0)) / 100;
                // const totalAmountPayable = IsOutBoundFundFlow(application?.fundFlowType || '')
                //   ? commitmentAmount + placementFeeAmount
                //   : commitmentAmount;
                const totalAmountPayable = commitmentAmount;

                setValues({
                  ...values,
                  planId: value as string,
                  commitmentAmount: selectedPlan?.minCommitmentAmount,
                  contributionChequeAmount: 0,
                  setupFeePercentage: selectedPlan?.setupFee,
                  hurdleRateOfReturns: '',
                  placementFee: selectedPlan?.placementFee,
                  totalExpense: selectedPlan?.totalExpense,
                  managementFeesRate: selectedPlan?.managementFee,
                  totalAmountPayable,
                  placementFeeAmount,
                  operatingExpenseApplicable: selectedPlan?.operatingExpenseApplicable,
                  operatingExpenseInWords: selectedPlan?.operatingExpenseInWords,
                  exitLoadApplicable: selectedPlan?.exitLoadApplicable,
                  exitLoadInWords: selectedPlan?.exitLoadInWords,
                  placementFeeType: selectedPlan?.placementFeeType,
                  isPlacementFeeApplicable: selectedPlan?.isPlacementFeeApplicable,
                  investmentPlanApplicable: selectedPlan?.investmentPlanApplicable,
                  investmentPlanType: selectedPlan?.investmentPlanType,
                  transactionCurrency: selectedPlan?.transactionCurrency,
                });
              }}
              searchFieldPlaceholder={'Search Plan Type'}
            />
          </Grid>
          {application?.applicationSource &&
          !folioValidationCheck(application?.applicationSource) ? (
            <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
              <MFTextField
                name={'commitmentAmount'}
                label="Capital Commitment *"
                type="number"
                placeholder="Enter Capital Commitment"
                onKeyDown={(e) => {
                  allowOnlyNumbers(e);
                }}
                onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                  const placementFeeAmount =
                    PlacementFeeApplicable(selectedPlan?.isPlacementFeeApplicable || '') &&
                    values?.placementFee
                      ? calculatePlacementFeeAmount(Number(value), Number(values.placementFee))
                      : 0;
                  const isOutbound = IsOutBoundFundFlow(fundFlowType);
                  setValues({
                    ...values,
                    commitmentAmount: value ? Number(value) : null,
                    contributionChequeAmount: 0,
                    placementFeeAmount: placementFeeAmount,
                    // totalAmountPayable: PlacementFeeApplicable(
                    //   selectedPlan?.isPlacementFeeApplicable || ''
                    // )
                    //   ? isOutbound
                    //     ? Number(value) + placementFeeAmount
                    //     : Number(value)
                    //   : 0,
                    totalAmountPayable: PlacementFeeApplicable(
                      selectedPlan?.isPlacementFeeApplicable || ''
                    )
                      ? Number(value)
                      : 0,
                  });
                }}
                trimOnBlur={false}
                disabled={isFieldDisabled}
              />
              {values.commitmentAmount != 0 && (
                <Typography sx={{ fontSize: 13 }}>
                  {currencyConversion(values.commitmentAmount, selectedPlan?.currency)}
                </Typography>
              )}
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
              <MFTextField
                name={'commitmentAmountForMigration'}
                label="Capital Commitment *"
                placeholder="Enter Capital Commitment"
                disabled={isFieldDisabled}
              />
            </Grid>
          )}
          {isFieldApplicable(values?.isPlacementFeeApplicable || '') && (
            <>
              <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
                <MFTextField
                  name={'placementFee'}
                  label="Placement Fee % *"
                  placeholder="Enter Placement Fee %"
                  onKeyDown={(e) => {
                    allowOnlyNumbers(e);
                  }}
                  type="number"
                  trimOnBlur={false}
                  onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                    const placementFeeAmount =
                      PlacementFeeApplicable(values?.isPlacementFeeApplicable || '') && value
                        ? calculatePlacementFeeAmount(
                            Number(values.commitmentAmount),
                            Number(value)
                          )
                        : 0;
                    const isOutbound = IsOutBoundFundFlow(fundFlowType);
                    setValues({
                      ...values,
                      contributionChequeAmount: 0,
                      placementFee: value ? Number(value) : null,
                      placementFeeAmount: placementFeeAmount,
                      // totalAmountPayable: PlacementFeeApplicable(
                      //   values?.isPlacementFeeApplicable || ''
                      // )
                      //   ? isOutbound
                      //     ? Number(values.commitmentAmount)
                      //     : placementFeeAmount + Number(values.commitmentAmount)
                      //   : 0,
                      totalAmountPayable: PlacementFeeApplicable(
                        values?.isPlacementFeeApplicable || ''
                      )
                        ? Number(values.commitmentAmount)
                        : 0,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
                <MFTextField
                  name={'placementFeeAmount'}
                  label={'Placement Fee Amount'}
                  type="number"
                  placeholder="Enter Placement Fee Amount"
                  onKeyDown={(e) => {
                    allowOnlyNumbers(e);
                  }}
                  trimOnBlur={false}
                  disabled={true}
                />
                {values.placementFeeAmount != 0 && (
                  <Typography sx={{ fontSize: 13 }}>
                    {currencyConversion(values.placementFeeAmount, selectedPlan?.currency)}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
                <MFTextField
                  name={'totalAmountPayable'}
                  label={'Total Amount Payable'}
                  type="number"
                  placeholder="Enter Total Amount Payable"
                  onKeyDown={(e) => {
                    allowOnlyNumbers(e);
                  }}
                  trimOnBlur={false}
                  disabled={true}
                />
                {values.totalAmountPayable != 0 && (
                  <Typography sx={{ fontSize: 13 }}>
                    {currencyConversion(values.totalAmountPayable, selectedPlan?.currency)}
                  </Typography>
                )}
              </Grid>
            </>
          )}
          {isFieldApplicable(values?.operatingExpenseApplicable || '') && (
            <KeyValue title={'Operating Expense'} description={values.operatingExpenseInWords} />
          )}

          {isFieldApplicable(values?.exitLoadApplicable || '') && (
            <KeyValue title={'Exit Load'} description={values.exitLoadInWords} />
          )}
          {isFieldApplicable(values?.investmentPlanApplicable || '') && (
            <KeyValue
              title={'Investment Plan'}
              description={investmentPlanTypeMaster[values.investmentPlanType || '']}
            />
          )}
          {/* <Grid item xs={12} sm={6}>
            <MFTextField
              name={'managementFeesRate'}
              label="Management Fee"
              placeholder="Enter Management Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
              disabled={isFieldDisabled}
            />
          </Grid> */}
          {/* <Grid item xs={12} sm={6}>
            <MFTextField
              name={'taxRate'}
              label="Tax Rate"
              placeholder="Enter Tax Rate"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MFSelectField
              name={'drawDownNumber'}
              label="Drawdown Number"
              items={DrawDownMasters.map((master) => ({ key: master, value: master }))}
            />
          </Grid> */}
          {/* {application?.applicationSource &&
          !folioValidationCheck(application?.applicationSource) ? (
            <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
              <MFTextField
                name={'contributionChequeAmount'}
                label="Contribution Amount *"
                placeholder="Enter Contribution Amount"
                type="number"
                onKeyDown={(e) => {
                  allowOnlyNumbers(e);
                }}
                // onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                //   const feeAmountObj = setUpFeeCalculations(
                //     values.commitmentAmount || 0,
                //     values.setupFeePercentage || 0
                //   );
                //   const netSetupFee = feeAmountObj.totalSetupFee - (values.setupFeeTDS || 0);
                //   setValues({
                //     ...values,
                //     contributionChequeAmount: value ? Number(value) : null,
                //     setupFeeAmount: feeAmountObj.feeAmount,
                //     setupFeeGST: feeAmountObj.feeGst,
                //     totalSetupFee: feeAmountObj.totalSetupFee,
                //     netSetupFeeLessTDS: netSetupFee,
                //     totalAmountPayable: Number(value) || values.commitmentAmount,
                //   });
                // }}
                trimOnBlur={false}
                disabled={isFieldDisabled}
              />
              {values.contributionChequeAmount != 0 && (
                <Typography sx={{ fontSize: 13 }}>
                  {currencyConversion(values.contributionChequeAmount, selectedPlan?.currency)}
                </Typography>
              )}
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
              <MFTextField
                name={'contributionAmountForMigration'}
                label="Contribution Amount *"
                placeholder="Enter Contribution Amount"
                disabled={isFieldDisabled}
              />
            </Grid>
          )} */}
          {/* <Grid item xs={12} sm={6}>
            <MFTextField
              name={'hurdleRateOfReturns'}
              label="Transaction Fee %"
              placeholder="Enter Transaction Fee %"
              disabled={isFieldDisabled}
            />
          </Grid> */}
          {/* <Grid item xs={12} sm={6}>
            <MFTextField
              name={'performanceFee'}
              label="Performance Fee %"
              placeholder="Enter Performance Fee %"
              disabled={isFieldDisabled}
            />
          </Grid> */}
          {/* <Grid item xs={12} sm={6}>
            <MFTextField
              name={'setupFeePercentage'}
              label="Setup Fee Percentage *"
              type="number"
              placeholder="Enter Setup Fee Percentage"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                const feeAmountObj = setUpFeeCalculations(
                  values.commitmentAmount || 0,
                  Number(value)
                );
                const netSetupFee = feeAmountObj.totalSetupFee - (values.setupFeeTDS || 0);
                setValues({
                  ...values,
                  setupFeePercentage: value ? Number(value) : null,
                  setupFeeAmount: feeAmountObj.feeAmount,
                  setupFeeGST: feeAmountObj.feeGst,
                  totalSetupFee: feeAmountObj.totalSetupFee,
                  netSetupFeeLessTDS: netSetupFee,
                  totalAmountPayable: values.contributionChequeAmount || values.commitmentAmount,
                });
              }}
              trimOnBlur={false}
              disabled={isFieldDisabled}
            />
          </Grid> */}
          {/* <Grid
            item
            xs={12}
            onClick={() => setPaymentOpen(!paymentOpen)}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              my: 2,
            }}>
            <Typography
              component="h3"
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 25,
                fontWeight: 500,
                color: 'secondary.main',
              }}>
              Payment Details
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: 30, transform: paymentOpen ? 'rotate(180deg)' : '' }} />
          </Grid> */}
          {/* {paymentOpen && (
            <>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={'paymentBankame'}
                  label="Payment Bank Name"
                  placeholder="Enter Payment Bank Name"
                  disabled={isFieldDisabled}
                />
              </Grid>
              <Grid item xs={12} sm={6} />
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={'paymentBankAccountNumber'}
                  label="Payment Bank Account Number"
                  placeholder="Enter Payment Bank Account Number"
                  disabled={isFieldDisabled}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ pt: '25px !important' }}>
                <UseRadioGroup
                  name={'paymentBankAccontType'}
                  formLabel="Payment Bank Account Type"
                  defaultValue="current"
                  items={[
                    { label: 'Current', value: 'current' },
                    { label: 'Savings', value: 'savings' },
                  ]}
                  disabled={isFieldDisabled}
                />
              </Grid>
              <Grid item xs={12}>
                <UseRadioGroup
                  name={'modeOfPayment'}
                  formLabel="Mode of Payment"
                  defaultValue="cheque"
                  items={[
                    { label: 'Cheque', value: 'cheque' },
                    { label: 'RTGS', value: 'rtgs' },
                    { label: 'Online Transfer', value: 'online-transfer' },
                    { label: 'Others', value: 'others' },
                  ]}
                  disabled={isFieldDisabled}
                />
              </Grid>
              {values.modeOfPayment === 'cheque' && (
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label={'Payment/Cheque Date'}
                    placeholder={'Enter Cheque Date'}
                    inputLabelStyles={{
                      transform: 'unset',
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'rgba(0,0,0,0.7)',
                    }}
                    name="chequeDate"
                    disabled={isFieldDisabled}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={'paymentRefNo'}
                  label="Payment Ref No"
                  placeholder="Enter Payment Ref No"
                  disabled={isFieldDisabled}
                />
              </Grid>
            </>
          )} */}
          <FieldValidationNote />
          <ProceedSaveLater
            goToPreviousStep={() => {
              checkIfApplicationIsNonIndividual(application as ApplicationProps)
                ? history.push('contact-details')
                : history.push('bank-details');
            }}
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Grid>
      )}
    </Formik>
  );
}
